import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Alert,
  Form,
  Dropdown,
  Badge,
} from 'react-bootstrap';
import {
  Building,
  CheckCircle,
  Envelope,
  ExclamationTriangle,
} from 'react-bootstrap-icons';
import { Organization } from '../../models';
import Logo from '../../res/MayorX.svg';

const CONTACT_EMAIL = process.env.REACT_APP_CONTACT_EMAIL || 'hola@mayorx.net';
const CONTACT_SUBJECT = 'Soporte / Ayuda - MayorX';

interface OrganizationSelectorProps {
  organizations: Organization[];
  onSelectOrganization: (organizationId: string) => Promise<void>;
  loading?: boolean;
  /** When true, user has orgs but all are deactivated – show message instead of list */
  allOrganizationsDeactivated?: boolean;
}

const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({
  organizations,
  onSelectOrganization,
  loading = false,
  allOrganizationsDeactivated = false,
}) => {
  const navigate = useNavigate();
  const [selectedOrganizationId, setSelectedOrganizationId] =
    useState<string>('');
  const [isSelecting, setIsSelecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedOrganization = organizations.find(
    org => org.id === selectedOrganizationId
  );

  const handleSelectOrganization = async () => {
    if (!selectedOrganizationId) return;

    setIsSelecting(true);
    setError(null);

    try {
      await onSelectOrganization(selectedOrganizationId);
      navigate('/home', { replace: true });
    } catch (error) {
      console.error('Error selecting organization:', error);
      setError(
        'Error al seleccionar la organización. Por favor, inténtalo de nuevo.'
      );
    } finally {
      setIsSelecting(false);
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-body text-body">
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col xs={12} md={8} lg={6} xl={4} className="min-w-0">
            {/* Header */}
            <div className="text-center mb-4">
              <img
                src={Logo}
                alt="Wholesale System Logo"
                className="mb-4 official-logo"
                style={{
                  width: '120px',
                  height: '120px',
                  objectFit: 'contain',
                }}
              />
              <h1 className="h3 mb-2 fw-bold">Seleccionar Organización</h1>
              <p className="text-muted">
                Elige la organización con la que deseas trabajar
              </p>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="text-center mb-4">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Cargando organizaciones...</p>
              </div>
            )}

            {/* Error Alert */}
            {error && (
              <Alert variant="danger" className="mb-4">
                <ExclamationTriangle className="me-2" />
                {error}
              </Alert>
            )}

            {/* Organization Selection Card */}
            {!loading && (
              <Card className="border shadow-sm overflow-hidden bg-body">
                <Card.Body className="p-4 min-w-0">
                  {allOrganizationsDeactivated ? (
                    <div className="mb-0">
                      <Alert variant="warning" className="mb-3">
                        <ExclamationTriangle className="me-2" />
                        <strong>Organización(es) desactivada(s).</strong> Tu
                        cuenta no tiene organizaciones activas en este momento.
                        Contacta al administrador del sistema para más
                        información.
                      </Alert>
                      <div className="d-grid">
                        <Button
                          as="a"
                          variant="primary"
                          size="lg"
                          href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(CONTACT_SUBJECT)}&body=${encodeURIComponent('Hola,\n\nMi(s) organización(es) aparecen desactivada(s). Necesito ayuda para reactivarla(s).\n\nGracias.')}`}
                          className="py-3 text-decoration-none"
                        >
                          <Envelope className="me-2" />
                          Contactar soporte
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Form.Group className="mb-4 min-w-0">
                        <Form.Label className="fw-semibold">
                          <Building className="me-2" />
                          Organización
                        </Form.Label>
                        <Dropdown className="w-100 min-w-0">
                          <Dropdown.Toggle
                            variant="outline-secondary"
                            size="lg"
                            disabled={organizations.length === 0}
                            className="w-100 d-flex justify-content-between align-items-center border-0 gap-2 min-w-0 text-start"
                            id="org-dropdown-org-select"
                          >
                            {selectedOrganization ? (
                              <>
                                <span
                                  className="text-truncate fw-bold min-w-0 flex-grow-1"
                                  title={selectedOrganization.name}
                                >
                                  {selectedOrganization.name}
                                </span>
                                {selectedOrganization.isSandbox && (
                                  <Badge
                                    bg="secondary"
                                    className="flex-shrink-0 fw-normal"
                                    style={{
                                      fontSize: '0.65rem',
                                      letterSpacing: '0.1em',
                                    }}
                                  >
                                    PRUEBAS
                                  </Badge>
                                )}
                              </>
                            ) : (
                              <span className="text-muted">
                                Selecciona una organización...
                              </span>
                            )}
                          </Dropdown.Toggle>
                          <Dropdown.Menu
                            className="w-100 shadow"
                            style={{ maxWidth: '100%' }}
                          >
                            {organizations.map(org => (
                              <Dropdown.Item
                                key={org.id}
                                active={org.id === selectedOrganizationId}
                                onClick={() =>
                                  setSelectedOrganizationId(org.id)
                                }
                                className="d-flex align-items-center gap-2 min-w-0"
                              >
                                <span
                                  className="text-truncate flex-grow-1 min-w-0 text-start"
                                  title={org.name}
                                >
                                  {org.name}
                                </span>
                                {org.isSandbox && (
                                  <Badge
                                    bg="secondary"
                                    pill
                                    className="flex-shrink-0 fw-normal"
                                    style={{
                                      fontSize: '0.65rem',
                                      letterSpacing: '0.06em',
                                    }}
                                  >
                                    Pruebas
                                  </Badge>
                                )}
                              </Dropdown.Item>
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>
                        {organizations.length === 0 && (
                          <Form.Text className="text-muted">
                            <ExclamationTriangle className="me-1" />
                            No tienes organizaciones asignadas. Contacta al
                            administrador.
                          </Form.Text>
                        )}
                      </Form.Group>

                      {/* Selected Organization Preview */}
                      {selectedOrganization && (
                        <div className="mb-4 p-3 rounded border min-w-0 overflow-hidden bg-body-tertiary">
                          <h6 className="mb-2 fw-semibold">
                            <CheckCircle className="me-2 text-success" />
                            Organización Seleccionada
                          </h6>
                          <p
                            className="mb-1 fw-medium text-break"
                            title={selectedOrganization.name}
                          >
                            {selectedOrganization.name}
                          </p>
                          {selectedOrganization.description && (
                            <p className="mb-2 text-muted small text-break">
                              {selectedOrganization.description}
                            </p>
                          )}
                          <div className="d-flex align-items-center">
                            <span
                              className={`badge ${
                                selectedOrganization.active
                                  ? 'bg-success'
                                  : 'bg-secondary'
                              }`}
                            >
                              {selectedOrganization.active
                                ? 'Activa'
                                : 'Inactiva'}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Action Button */}
                      <div className="d-grid">
                        <Button
                          variant="primary"
                          size="lg"
                          onClick={handleSelectOrganization}
                          disabled={
                            !selectedOrganizationId ||
                            organizations.length === 0 ||
                            isSelecting
                          }
                          className="py-3"
                        >
                          {isSelecting ? (
                            <>
                              <Spinner
                                animation="border"
                                size="sm"
                                className="me-2"
                              />
                              Seleccionando...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="me-2" />
                              CONTINUAR
                            </>
                          )}
                        </Button>
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>
            )}

            {/* Help Text + Contact */}
            <div className="text-center mt-4">
              <small className="text-muted d-block mb-2">
                ¿No ves tu organización?
              </small>
              <Button
                as="a"
                variant="outline-primary"
                size="sm"
                href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(CONTACT_SUBJECT)}`}
                className="text-decoration-none"
              >
                <Envelope className="me-1" />
                Contactar soporte
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default OrganizationSelector;
